import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ReactComponent as ArrowBack } from './icons/arrow-back.svg'
import { ReactComponent as ArrowDown } from './icons/arrow-down.svg'
import { ReactComponent as Close } from './icons/close.svg'

import Input from '../../../../Forms/Input'
import Carousel, { CarouselSlide } from '../../../../Carousel'

import fpTest from '../../../../../assets/images/fp.png'
import Dropdown from '../../../../Forms/Dropdown'
import SearchInput from '../../../../Forms/SearchInput'
import { findEntranceLevel } from '../../../../../helpers/levels'
import { getHumanLabelBathrooms, getHumanLabelBedrooms, getLineName } from '../../../../../helpers/line'
import { getCdnAssetUrl } from '../../../../../helpers/assets'
import { smartSort } from '../../../../../helpers/arrays'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { formatPrice } from '../../../../../helpers/price'
import { captureEvent } from '../../../../../analytics'
import { analyticsEvents } from '../../../../../analytics/events'
import { AppConfigContext } from '../../../../../context/AppConfig'
import Slider from './components/Slider'
import { LinesLoadingIndicator } from '..'

const options = [
  { value: 'chocolate', label: 'Chocolate' },
  { value: 'strawberry', label: 'Strawberry' },
  { value: 'vanilla', label: 'Vanilla' },
]

function FloorPlanPicker({ selectedPlan, lines, onClose, onSelect, shown = true, property}) {


  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    setMounted(shown)
  }, [shown])

  const ref = useDetectClickOutside({
    onTriggered: () => {
      if (mounted && shown) {
        onClose()
      }
    },
  })

  useEffect(() => {
      if (shown) {
        document.body.classList.add('freeze')
      } else {
        document.body.classList.remove('freeze')
      }
  
      return () => {
        document.body.classList.remove('freeze')
      }
    }, [shown])

  return (
    <div
      className={`fixed left-0 top-0 h-[calc(100%-30px)] w-[calc(100%-var(--sidebar-shown-width))] flex justify-center items-center z-[100] ${
        !shown ? '!hidden' : ''
      }`}
    >
      <div
        className="bg-white border-[1px] border-solid border-[#C7CCD9] h-[calc(100%-46px)] w-[calc(100%-46px)] overflow-x-hidden overflow-y-auto z-[100] p-[24px] !rounded-[8px] relative"
        onClick={(e) => e.stopPropagation()}
        ref={ref}
      >
       
        <FloorPlanPickerContent
          lines={lines}
          onClose={() => {
            onClose()
            captureEvent(analyticsEvents.FLOOR_PLAN_SELECTION_SCREEN_CLOSE)
          }}
          shown={shown}
          selectedPlan={selectedPlan}
          onSelect={onSelect}
        />
         {property?.lines_preloading && 
            <div className='absolute top-0 left-0 h-full w-full my-[128px]  opacity-80 bg-white backdrop-blur-xl flex items-center justify-center'>
            <LinesLoadingIndicator size={64}/>
          </div>
        }
     
      </div>
    </div>
  )
}

export function FloorPlanPickerContent({ onClose, lines, onSelect, selectedPlan, shown }) {
  const [pickedBedroomFilter, setPickedBedroomFilter] = useState()
  const [pickedLineFilter, setPickedLineFilter] = useState()
  const [searchFilter, setSearchFilter] = useState()

  const [linesToggleState, setLinesToggleState] = useState({})

  const availableBedroomsUnitTypes = useMemo(() => {
    const bedrooms = []
    lines.forEach((l) => {
      if (!bedrooms.includes(l.data.number_of_bedrooms)) {
        bedrooms.push(l.data.number_of_bedrooms)
      }
    })
    return bedrooms
  }, [lines])

  const bedroomOptions = useMemo(() => {
    const bedrooms = availableBedroomsUnitTypes.map((i) => ({ value: i, label: i }))
    return [...[{ label: 'All', value: null }], ...bedrooms]
  }, [availableBedroomsUnitTypes])

  const getSlidesForLine = useCallback(
    (i, skipLineFilter) => {
      const bedroomCountMatchingLines = lines
        .filter((l) => l.data.number_of_bedrooms === i)
        .filter((l) => skipLineFilter || (pickedLineFilter ? l.data.id === pickedLineFilter : true))
      const entranceUnitPlans = bedroomCountMatchingLines.map((l) => ({
        level: findEntranceLevel(l.data.unit_type_levels),
        line: l.data,
      }))

      function getPackageEntranceFloorPlan(pack, entranceLevelId) {
        const mostCheapPackageEntraceFloorPlans = pack.unit_plans.filter(
          (u) => u.unit_type_level?.id === entranceLevelId,
        )
        // Get floor plan preferable with angle, if not, fallback to first one
        return mostCheapPackageEntraceFloorPlans.find((u) => u.contains_angle) || mostCheapPackageEntraceFloorPlans[0]
      }

      return entranceUnitPlans
        .map((p) => {
          const prices = p.line.packages.map((p) => p.price)

          //==========================================================================
          // Most cheap package
          //==========================================================================
          // Get most cheap package price
          const mostCheapPackagePrice = Math.min(...prices)

          // Get most cheap package
          const mostCheapPackage = p.line.packages.find((p) => p.price === mostCheapPackagePrice)

          // Get floor plan preferable with angle, if not, fallback to first one
          const mostCheapFloorPlan = getPackageEntranceFloorPlan(mostCheapPackage, p.level.id)
          // Return nothing if plan is not found

          const src = mostCheapFloorPlan.src

          return {
            line: p.line,
            content: (
              <CarouselSlide key={`${mostCheapFloorPlan.id}`} style={{ width: 224 }} className="mr-[20px]">
                <button
                  className="text-left"
                  type="button"
                  onClick={() => {
                    onSelect(mostCheapFloorPlan, p.line, mostCheapPackage, p.level)
                  }}
                >
                  <div
                    className={`w-[224px] border-[1px] border-solid ${
                      selectedPlan?.id === mostCheapFloorPlan.id ? 'border-[var(--brand-color)]' : 'border-[#C7CCD9]'
                    } rounded-[6px] overflow-hidden`}
                  >
                    <div className="flex items-center justify-center">
                      {src ? (
                        <img src={getCdnAssetUrl(src)} alt="fp" className="h-[150px] w-auto" />
                      ) : (
                        <div className="h-[200px] aspect-square flex items-center justify-center opacity-30 font-bold">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-[16px] border-t-[1px] border-solid font-medium border-[#C7CCD9]">
                      <div>
                        {getLineName(p.line)}
                      </div>
                      <div className="text-[#3D4256] opacity-80 text-[14px]">
                        {[getHumanLabelBedrooms(p.line), getHumanLabelBathrooms(p.line)].join(' · ')}
                        {p.line.size_to_display ? ` · ${p.line.size_to_display} ft sq` : ''}
                      </div>
                    </div>
                  </div>
                </button>
              </CarouselSlide>
            ),
          }
        })
        .filter((v) => !!v)
    },
    [lines, onSelect, pickedLineFilter, selectedPlan],
  )

  const lineOptions = useMemo(() => {
    const linesOptions = lines
      .filter((l) => (pickedBedroomFilter ? l.data.number_of_bedrooms === pickedBedroomFilter : true))
      .filter((l) => {
        return getSlidesForLine(l.data.number_of_bedrooms, true).find((c) => c.line.id === l.data.id)
      })
      .map((l) => ({ label: getLineName(l.data), value: l.data.id }))
      .sort(smartSort('label'))

    return [...[{ label: 'All', value: null }], ...linesOptions]
  }, [lines, pickedBedroomFilter, getSlidesForLine])

  function numberToName(num) {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
    return ones[num] || num
  }

  function getCheapestPackagePrice(bedroomsCount) {
    const bedroomCountMatchingLines = lines.filter((l) => l.data.number_of_bedrooms === bedroomsCount)
    return bedroomCountMatchingLines
      .map((l) => l.data.packages)
      .flat()
      .map((p) => p.price)
      .reduce((a, b) => Math.min(a, b))
  }

  function toggleLine(line) {
    setLinesToggleState((state) => ({
      ...state,
      [line]: state[line] === undefined ? false : !state[line],
    }))
  }

  function isToggled(i) {
    return linesToggleState[numberToName(i)] === undefined || linesToggleState[numberToName(i)] === true
  }

  const { show_package_price, hide_prices } = useContext(AppConfigContext)

  const [sliders, setSliders] = useState({})


  return (
    <>
      <div className="flex items-center justify-between">
        <div className="text-[20px] mt-[2px] font-semibold text-[var(--brand-color)]">Choose Floor Plan</div>
        <button type="button" className="hidden lg:inline-block" onClick={onClose}>
          <Close />
        </button>
      </div>
      {/* <div className="grid grid-cols-2 lg:grid-cols-3 gap-[16px] lg:gap-x-[18px] lg:gap-y-[0] mt-[32px] pr-[30px] relative z-[50]">
  <SearchInput
    value={searchFilter}
    onChange={(e) => setSearchFilter(e.target.value)}
    className="col-span-2 lg:col-span-1"
  />
  <Dropdown
    value={pickedBedroomFilter && bedroomOptions.find((l) => l.id === pickedBedroomFilter)}
    placeholder="Bedroom #"
    options={bedroomOptions}
    onChange={({ value }) => {
      setPickedBedroomFilter(value)
      setPickedLineFilter(null)
    }}
  />
  <Dropdown
    value={pickedLineFilter && lineOptions.find((l) => l.id === pickedLineFilter)}
    placeholder="Line"
    options={lineOptions}
    onChange={({ value }) => setPickedLineFilter(value)}
  />
</div> */}
      {availableBedroomsUnitTypes
        .filter((i) => (pickedBedroomFilter ? i === pickedBedroomFilter : true))
        .filter((i) =>
          pickedLineFilter ? i === lines.find((l) => l.data.id === pickedLineFilter).data.number_of_bedrooms : true,
        )
        .filter((i) =>
          searchFilter
            ? String(i).includes(searchFilter) || numberToName(i).toLowerCase().includes(searchFilter.toLowerCase())
            : true,
        )
        .map((i, index) => (
          <>
            <div key={index} className="mt-[20px] lg:mt-[40px] flex justify-between items-center pr-[30px]">
              <div className="flex items-center justify-between lg:justify-start w-full">
                <div>
                  <div className="text-[14px] text-[#3D4256] font-medium mr-[8px]">
                    {i === 0 ? 'Studio' : `${numberToName(i)} Bedroom Apartments`}
                  </div>
                  <div className="text-[14px] lg:hidden text-[#3D4256] opacity-70">
                    Packages {!show_package_price ? 'starting at ' : ''}${getCheapestPackagePrice(i)}/m
                  </div>
                </div>
                <button
                  type="button"
                  className={`${isToggled(i) ? '' : 'rotate-[-90deg]'}`}
                  onClick={() => toggleLine(numberToName(i))}
                >
                  <ArrowDown />
                </button>
              </div>
              {!hide_prices && <div className="text-[14px] hidden lg:block flex-shrink-0 text-[#3D4256] font-medium">
                Packages {!show_package_price ? 'starting at ' : ''} ${formatPrice(getCheapestPackagePrice(i))}/mo
              </div>}
            </div>
            <div className="h-[1px] bg-[#C7CCD9] my-[12px]"></div>
            {isToggled(i) && (
              <div className="mt-[24px] pb-[24px] lg:mb-0 w-full ">
                <Slider items={getSlidesForLine(i)} shown={shown} hideSidePadding={Object.values(sliders).length > 0 && Object.values(sliders).every(val=>!val.hasScroll)} onUpdate={u=>setSliders(s=>({...s, [index]: u}))}/>
              </div>
            )}
          </>
        ))}
    </>
  )
}

export default FloorPlanPicker
